import axios from 'axios';

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

const client = axios.create({ baseURL: API });

function authHeaders() {
  const token = localStorage.getItem('ac_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Sent on menu reads so a signed-in company sees its extra products and
// collection points. Absent for everyone else, which is what hides them —
// the filtering happens in the database query, not in the page.
function companyHeaders() {
  const t = localStorage.getItem('ac_company_token');
  return t ? { 'X-Company-Token': t } : {};
}

export const api = {
  // public
  getDishes: () => client.get('/dishes', { headers: companyHeaders() }).then((r) => r.data),
  getDish: (id) => client.get(`/dishes/${id}`, { headers: companyHeaders() }).then((r) => r.data),
  getFridges: () => client.get('/fridges', { headers: companyHeaders() }).then((r) => r.data),
  getSettings: () => client.get('/settings').then((r) => r.data),
  // Cache-busted: the checkout must render the payment block the server will
  // act on, never one a proxy or the browser remembered.
  getSettingsFresh: () => client.get('/settings', { params: { t: Date.now() } }).then((r) => r.data),
  createOrder: (payload) => client.post('/orders', payload).then((r) => r.data),
  getPreorderWindow: () => client.get('/preorder-window').then((r) => r.data),
  companyLogin: (code) => client.post('/company/login', { code }).then((r) => r.data),
  companyMe: () => client.get('/company/me', { headers: companyHeaders() }).then((r) => r.data),
  companyLogout: () => client.post('/company/logout', {}, { headers: companyHeaders() }).then((r) => r.data),
  listCompanies: () => client.get('/admin/companies', { headers: authHeaders() }).then((r) => r.data),
  createCompany: (body) => client.post('/admin/companies', body, { headers: authHeaders() }).then((r) => r.data),
  updateCompany: (id, body) => client.put(`/admin/companies/${id}`, body, { headers: authHeaders() }).then((r) => r.data),
  getPaymentStatus: (token) => client.get(`/orders/payment/${token}`).then((r) => r.data),

  // admin
  login: (password) => client.post('/admin/login', { password }).then((r) => r.data),
  logout: () => client.post('/admin/logout', {}, { headers: authHeaders() }).then((r) => r.data).catch(() => ({})),
  weeklySummary: (monday = '') => client.get(`/admin/weekly-summary${monday ? `?monday=${monday}` : ''}`, { headers: authHeaders() }).then((r) => r.data),
  sendWeeklySummary: (monday = '') => client.post(`/admin/weekly-summary/send${monday ? `?monday=${monday}` : ''}`, {}, { headers: authHeaders() }).then((r) => r.data),
  askAssistant: (message, session_id, lang) => client.post('/assistant', { message, session_id, lang }).then((r) => r.data),
  enrichDescriptions: () => client.post('/ai/enrich', {}, { headers: authHeaders() }).then((r) => r.data),
  updateDish: (id, patch) => client.put(`/dishes/${id}`, patch, { headers: authHeaders() }).then((r) => r.data),
  uploadDishPhoto: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return client.post(`/dishes/${id}/photo`, fd, { headers: authHeaders() }).then((r) => r.data);
  },
  createDish: (dish) => client.post('/dishes', dish, { headers: authHeaders() }).then((r) => r.data),
  deleteDish: (id) => client.delete(`/dishes/${id}`, { headers: authHeaders() }).then((r) => r.data),
  sync: () => client.post('/sync', {}, { headers: authHeaders() }).then((r) => r.data),
  reopen: () => client.post('/reopen', {}, { headers: authHeaders() }).then((r) => r.data),
  rotateFridge: () => client.post('/rotate-fridge', {}, { headers: authHeaders() }).then((r) => r.data),
  replacePreorderMenu: (dryRun) => client.post('/admin/replace-preorder-menu', {}, { params: { dry_run: dryRun }, headers: authHeaders() }).then((r) => r.data),
  rotationStatus: () => client.get('/admin/rotation-status', { headers: authHeaders() }).then((r) => r.data),
  detailsHealth: () => client.get('/admin/details-health', { headers: authHeaders() }).then((r) => r.data),
  emailHealth: () => client.get('/admin/email-health', { headers: authHeaders() }).then((r) => r.data),
  payrexxHealth: (refresh = false) => client.get('/admin/payrexx-health', { params: refresh ? { refresh: 1 } : {}, headers: authHeaders() }).then((r) => r.data),
  refreshDetails: (scope = 'all') => client.post(`/admin/refresh-details?scope=${scope}`, {}, { headers: authHeaders() }).then((r) => r.data),
  archiveIndex: () => client.get('/admin/archive', { headers: authHeaders() }).then((r) => r.data),
  // Exports come back as files, so they need the auth header and a blob —
  // a plain link would hit the endpoint unauthenticated.
  downloadExport: async (path, filename) => {
    const res = await client.get(path, { headers: authHeaders(), responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  },
  getDishesAdmin: () => client.get('/admin/dishes', { headers: authHeaders() }).then((r) => r.data),
  getOrders: () => client.get('/orders', { headers: authHeaders() }).then((r) => r.data),
  updateOrder: (id, status) => client.put(`/orders/${id}`, { status }, { headers: authHeaders() }).then((r) => r.data),
  markOrderPaid: (id, paid = true) => client.post(`/orders/${id}/mark-paid?paid=${paid}`, {}, { headers: authHeaders() }).then((r) => r.data),
};

export default api;
